import { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Receipt } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { BTW_RATE_OPTIONS, BTW_RATE_MAX, BTW_AFTREK_OPTIONS, btwRegime, isCustomRate } from "@/lib/btw";

const CATS = [["revenue", "Inkomsten"], ["opex", "Kosten"], ["other_income", "Overige baten"]];

// Tariefkeuze per subpost: Standaard / 21 / 9 / Geen / Effectief % (vrij percentage voor gemengde posten).
function RateSelect({ t, sp, defaultRate, canEdit, onSave, exemptLabel }) {
  const custom = isCustomRate(sp.btw_rate);
  const [mode, setMode] = useState(custom ? "custom" : sp.btw_rate == null ? "" : String(Number(sp.btw_rate)));
  const [draft, setDraft] = useState(custom ? String(sp.btw_rate) : "");
  const tid = `${sp.category}-${sp.name}`;
  const pick = (v) => {
    setMode(v);
    if (v === "custom") { setDraft(custom ? String(sp.btw_rate) : ""); return; }
    onSave(v === "" ? null : Number(v));
  };
  const commit = () => {
    const n = Number(String(draft).replace(",", "."));
    if (!Number.isFinite(n) || n < 0 || n > BTW_RATE_MAX) { toast.error(t("Effectief tarief moet tussen 0 en 21 liggen.")); return; }
    if (Number(sp.btw_rate) !== n) onSave(n);
  };
  return (
    <div className="flex items-center gap-2">
      <select value={mode} disabled={!canEdit} onChange={(e) => pick(e.target.value)} data-testid={`btw-sub-rate-${tid}`}
        className="border-2 border-foreground bg-background px-2 py-1 text-sm flex-1 min-w-[150px]">
        {BTW_RATE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.value === "" ? `${t("Standaard")} · ${defaultRate} %` : o.value === "0" && exemptLabel ? t(exemptLabel) : t(o.label)}
          </option>
        ))}
      </select>
      {mode === "custom" && (
        <label className="inline-flex items-center gap-1 font-mono-num text-sm">
          <input type="number" min={0} max={BTW_RATE_MAX} step={0.1} value={draft} disabled={!canEdit} autoFocus={!custom}
            onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            data-testid={`btw-sub-custom-${tid}`} className="w-20 border-2 border-foreground bg-background px-2 py-1 text-right" placeholder="14" />%
        </label>
      )}
    </div>
  );
}

// Clubtab "BTW": regime (vrijgesteld / plichtig / gemengd), standaardtarief, pro rata en tarief/aftrek per subpost.
export function BtwSettingsCard({ t, clubId, club, subposts, canEdit, onClubSaved, onSubpostsChanged }) {
  const [busy, setBusy] = useState(false);
  const regime = btwRegime(club);
  const mixed = regime === "gemengd";
  const defaultRate = club?.btw_default_rate == null ? 21 : Number(club.btw_default_rate);
  const defaultAftrek = club?.btw_default_aftrek || "pro_rata";
  const tijdvak = club?.btw_aangiftetijdvak === "jaar" ? "jaar" : "kwartaal";
  const proRata = club?.btw_pro_rata == null ? "" : String(club.btw_pro_rata);
  const [proRataDraft, setProRataDraft] = useState(proRata);

  const save = async (next) => {
    setBusy(true);
    try {
      const body = {
        btw_regime: next.regime ?? regime,
        btw_default_rate: next.rate ?? defaultRate,
        btw_pro_rata: next.proRata !== undefined ? next.proRata : (club?.btw_pro_rata ?? null),
        btw_default_aftrek: next.aftrek ?? defaultAftrek,
        btw_aangiftetijdvak: next.tijdvak ?? tijdvak,
      };
      if (body.btw_regime === "gemengd" && body.btw_pro_rata == null) body.btw_pro_rata = 20;
      const { data } = await api.patch(`/clubs/${clubId}/btw`, body);
      onClubSaved?.(data);
      setProRataDraft(data.btw_pro_rata == null ? "" : String(data.btw_pro_rata));
      if (next.regime && next.regime !== regime) {
        const tagged = data.btw_auto_tagged ? ` ${t("{n} subpost(en) automatisch op 'geen BTW' gezet.").replace("{n}", data.btw_auto_tagged)}` : "";
        toast.success(next.regime === "vrijgesteld" ? t("Club staat op vrijgesteld — alle bedragen gelden nu als incl. BTW.")
          : next.regime === "plichtig" ? t("Club staat op BTW-plichtig — alle bedragen gelden nu als excl. BTW.") + tagged
          : t("Club staat op gemengd BTW-regime — alle bedragen gelden nu als excl. BTW. Controleer het pro-rata-percentage en de aftrek per kostenpost.") + tagged);
        if (next.regime !== "vrijgesteld") onSubpostsChanged?.();
      } else {
        toast.success(next.confirm ? t("BTW-situatie bevestigd — stap afgevinkt in het stappenplan.") : t("BTW-instelling opgeslagen"));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e));
    } finally {
      setBusy(false);
    }
  };
  const switchRegime = (next) => {
    if (next === regime || !canEdit) return;
    const msg = next === "vrijgesteld"
      ? t("Overschakelen naar vrijgesteld? Bestaande bedragen worden NIET omgerekend; vanaf nu gelden alle € als inclusief BTW. Het BTW-blok verdwijnt.")
      : next === "plichtig"
        ? t("Overschakelen naar BTW-plichtig? Bestaande bedragen worden NIET omgerekend; vanaf nu gelden alle € als exclusief BTW. Controleer je cijfers.")
        : t("Overschakelen naar gemengd BTW-regime? Bestaande bedragen worden NIET omgerekend; alle € gelden als exclusief BTW. Niet-aftrekbare BTW wordt in scenario's als kostenpost meegerekend.");
    if (window.confirm(msg)) save({ regime: next });
  };
  const commitProRata = () => {
    const n = Number(String(proRataDraft).replace(",", "."));
    if (!Number.isFinite(n) || n < 0 || n > 100) { toast.error(t("Pro-rata-percentage moet tussen 0 en 100 liggen.")); return; }
    if (Number(club?.btw_pro_rata) !== n) save({ proRata: n });
  };
  const patchSub = async (sp, body, okMsg) => {
    try {
      await api.patch(`/subposts/${sp.id}/btw`, body);
      onSubpostsChanged?.();
      toast.success(okMsg);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e));
    }
  };

  const Option = ({ value, title, text, testid }) => {
    const active = regime === value;
    return (
      <button type="button" onClick={() => switchRegime(value)} disabled={!canEdit || busy} data-testid={testid} aria-pressed={active}
        className={`text-left border-2 p-4 transition-colors ${active ? "border-foreground bg-primary/10 heavy-shadow" : "border-foreground/30 bg-card hover:border-foreground"} ${!canEdit ? "cursor-default" : ""}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="font-display uppercase font-black text-lg">{title}</div>
          {active && <span className="inline-flex items-center gap-1 text-[10px] font-display uppercase font-bold bg-foreground text-background px-2 py-0.5"><Check className="w-3 h-3" /> {t("Actief")}</span>}
        </div>
        <p className="text-sm text-muted-foreground mt-1">{text}</p>
      </button>
    );
  };

  return (
    <div className="space-y-6" data-testid="btw-settings">
      <div>
        <div className="stat-label">{t("Club")}</div>
        <h2 className="font-display font-black text-2xl uppercase tracking-tight inline-flex items-center gap-2"><Receipt className="w-6 h-6" /> {t("BTW")}</h2>
        <p className="text-sm text-muted-foreground max-w-3xl mt-1">
          {t("Verenigingen zijn meestal vrijgesteld van BTW: dan vul je alle bedragen inclusief BTW in, want de BTW is voor de club gewoon een kostenpost. Is de club (deels) BTW-plichtig, dan vul je alles exclusief BTW in en rekent de app de BTW 'onder de streep' uit: bij volledig BTW-plichtig informatief, bij een gemengd regime telt het niet-aftrekbare deel in scenario's als kostenpost.")}
        </p>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Option value="vrijgesteld" testid="btw-regime-vrijgesteld" title={t("Vrijgesteld van BTW")}
          text={t("Standaard voor sportverenigingen. Alle € inclusief BTW. Geen BTW-blok in de tabellen.")} />
        <Option value="plichtig" testid="btw-regime-plichtig" title={t("BTW-plichtig")}
          text={t("Alle € exclusief BTW. Onder Voorgaande jaren, Begroting en in scenario's een informatief BTW-blok: af te dragen, voorbelasting en saldo.")} />
        <Option value="gemengd" testid="btw-regime-gemengd" title={t("Gemengd")}
          text={t("Belaste én vrijgestelde inkomsten (bijv. motorboot- naast zeilbootligplaatsen). Voorbelasting pro rata aftrekbaar; het niet-aftrekbare deel telt in scenario's als kostenpost.")} />
      </div>
      {busy && <div className="text-xs text-muted-foreground inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {t("Opslaan…")}</div>}

      {canEdit && !club?.btw_confirmed && (
        <div className="border-2 border-primary bg-primary/5 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="btw-confirm-box">
          <p className="text-sm max-w-2xl">
            <span className="font-bold">{t("Klopt dit voor jouw club?")}</span>{" "}
            {t("Nog niet bevestigd. Kies hierboven het juiste regime of bevestig de huidige keuze — dan vinkt het stappenplan deze stap af.")}
          </p>
          <button type="button" onClick={() => save({ confirm: true })} disabled={busy} data-testid="btw-confirm-btn"
            className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2">
            <Check className="w-4 h-4" /> {t("Bevestig: {regime}").replace("{regime}", regime === "vrijgesteld" ? t("vrijgesteld") : regime === "plichtig" ? t("BTW-plichtig") : t("gemengd"))}
          </button>
        </div>
      )}

      {regime !== "vrijgesteld" && (
        <div className="space-y-5" data-testid="btw-plichtig-settings">
          <div className="border-2 border-foreground bg-card p-4 flex flex-wrap items-end gap-4">
            <div>
              <label className="stat-label block" htmlFor="btw-default-rate">{t("Standaardtarief")}</label>
              <select id="btw-default-rate" value={String(defaultRate)} disabled={!canEdit || busy} onChange={(e) => save({ rate: Number(e.target.value) })}
                data-testid="btw-default-rate" className="mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num">
                {BTW_RATE_OPTIONS.filter((o) => o.value !== "" && o.value !== "custom").map((o) => <option key={o.value} value={o.value}>{t(o.label)}</option>)}
              </select>
            </div>
            {mixed && (
              <>
                <div>
                  <label className="stat-label block" htmlFor="btw-pro-rata">{t("Pro-rata-percentage")}</label>
                  <div className="mt-1 inline-flex items-center gap-1 font-mono-num">
                    <input id="btw-pro-rata" type="number" min={0} max={100} step={0.1} value={proRataDraft} disabled={!canEdit || busy}
                      onChange={(e) => setProRataDraft(e.target.value)} onBlur={commitProRata} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      data-testid="btw-pro-rata" className="w-24 border-2 border-foreground bg-background px-3 py-2 text-right" />%
                  </div>
                </div>
                <div>
                  <label className="stat-label block" htmlFor="btw-default-aftrek">{t("Standaardaftrek kosten")}</label>
                  <select id="btw-default-aftrek" value={defaultAftrek} disabled={!canEdit || busy} onChange={(e) => save({ aftrek: e.target.value })}
                    data-testid="btw-default-aftrek" className="mt-1 border-2 border-foreground bg-background px-3 py-2">
                    {BTW_AFTREK_OPTIONS.filter((o) => o.value !== "").map((o) => <option key={o.value} value={o.value}>{t(o.label)}</option>)}
                  </select>
                </div>
              </>
            )}
            <p className="text-xs text-muted-foreground max-w-xl">
              {mixed
                ? t("Het pro-rata-percentage is het aandeel belaste haveninkomsten (bijv. liggelden motorboten ÷ alle liggelden). Het bepaalt welk deel van de voorbelasting op 'pro rata'-kosten en -investeringen aftrekbaar is. De standaardaftrek geldt voor kosten-subposten zonder eigen keuze en voor Overige kosten.")
                : t("Geldt voor subposten zonder eigen tarief én voor de restposten Overige inkomsten / Overige kosten en voor investeringen in scenario's. Afschrijvingen, rente en Kas + bank bevatten nooit BTW.")}
            </p>
          </div>

          <div className="border-2 border-foreground bg-card p-4 flex flex-wrap items-end gap-4" data-testid="btw-tijdvak-box">
            <div>
              <label className="stat-label block" htmlFor="btw-tijdvak">{t("Aangiftetijdvak")}</label>
              <select id="btw-tijdvak" value={tijdvak} disabled={!canEdit || busy} onChange={(e) => save({ tijdvak: e.target.value })}
                data-testid="btw-tijdvak" className="mt-1 border-2 border-foreground bg-background px-3 py-2">
                <option value="kwartaal">{t("Kwartaalaangifte")}</option>
                <option value="jaar">{t("Jaaraangifte")}</option>
              </select>
            </div>
            <p className="text-xs text-muted-foreground max-w-xl" data-testid="btw-tijdvak-note">
              {t("Bepaalt de BTW-voorfinanciering in scenario's: het deel van het BTW-saldo dat pas in het volgende jaar met de Belastingdienst wordt verrekend. Bij kwartaalaangifte is dat ongeveer het laatste kwartaal (25 %), bij jaaraangifte het hele jaarsaldo (100 %). Vooral bij investeringen zie je zo in het Kasstroomtraject hoe diep de bankstand tijdelijk zakt doordat je de BTW voorschiet.")}
            </p>
          </div>

          <div className="border-2 border-foreground bg-card heavy-shadow overflow-x-auto">
            <table className="w-full text-sm" data-testid="btw-subposts-table">
              <thead className="bg-muted/50 border-b-2 border-foreground">
                <tr>
                  <th className="text-left px-3 py-2 stat-label">{t("Subpost")}</th>
                  <th className="text-left px-3 py-2 stat-label w-80">{t("BTW-tarief")}</th>
                  {mixed && <th className="text-left px-3 py-2 stat-label w-56">{t("Aftrek voorbelasting")}</th>}
                </tr>
              </thead>
              <tbody>
                {CATS.map(([cat, label]) => {
                  const list = (subposts || []).filter((s) => s.category === cat);
                  const cols = mixed ? 3 : 2;
                  return [
                    <tr key={`${cat}-h`} className="bg-muted/30 border-t border-foreground/10"><td colSpan={cols} className="px-3 py-1.5 font-display uppercase font-black text-xs tracking-widest">{t(label)}</td></tr>,
                    ...(list.length ? list.map((sp) => (
                      <tr key={sp.id} className="border-b border-foreground/10" data-testid={`btw-sub-row-${sp.category}-${sp.name}`}>
                        <td className="px-3 py-1.5">{sp.name}
                          {sp.btw_rate === 0 && <span className="ml-2 text-[10px] font-display uppercase text-muted-foreground">{t(mixed && cat !== "opex" ? "vrijgesteld" : "geen BTW")}</span>}
                          {isCustomRate(sp.btw_rate) && <span className="ml-2 text-[10px] font-display uppercase text-muted-foreground">{t("effectief")} {Number(sp.btw_rate)} %</span>}
                        </td>
                        <td className="px-3 py-1.5">
                          <RateSelect key={`${sp.id}-${sp.btw_rate}`} t={t} sp={sp} defaultRate={defaultRate} canEdit={canEdit}
                            exemptLabel={mixed && cat !== "opex" ? "Vrijgesteld (geen BTW)" : null}
                            onSave={(v) => patchSub(sp, { btw_rate: v }, t("BTW-tarief van {n} opgeslagen").replace("{n}", sp.name))} />
                        </td>
                        {mixed && (
                          <td className="px-3 py-1.5">
                            {cat === "opex" ? (
                              <select value={sp.btw_aftrek || ""} disabled={!canEdit} data-testid={`btw-sub-aftrek-${sp.category}-${sp.name}`}
                                onChange={(e) => patchSub(sp, { btw_aftrek: e.target.value || null }, t("Aftrek van {n} opgeslagen").replace("{n}", sp.name))}
                                className="border-2 border-foreground bg-background px-2 py-1 text-sm w-full">
                                {BTW_AFTREK_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.label)}{o.value === "" ? ` · ${t((BTW_AFTREK_OPTIONS.find((x) => x.value === defaultAftrek) || {}).label || "")}` : ""}</option>)}
                              </select>
                            ) : <span className="text-xs text-muted-foreground">—</span>}
                          </td>
                        )}
                      </tr>
                    )) : [<tr key={`${cat}-e`}><td colSpan={cols} className="px-3 py-1.5 text-xs text-muted-foreground italic">{t("Nog geen subposten — voeg ze toe bij Voorgaande jaren.")}</td></tr>]),
                  ];
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground max-w-3xl" data-testid="btw-advice">
            {t("Let op: het BTW-blok is een schatting — uit een subposttotaal is niet af te leiden welk deel 9 % en welk deel 21 % is. Splits gemengde posten (bijv. Kantine laag / Kantine hoog) of kies 'Effectief %' en leid het percentage af uit je laatste aangifte (afgedragen BTW ÷ omzet van die post). Bankkosten, boetes, rente en verzekeringspremies krijgen op naam automatisch 'geen BTW'; controleer zelf of contributies, subsidies en donaties bij jouw club buiten de BTW vallen.")}
            {mixed && ` ${t("Bij het gemengde regime: zet vrijgestelde inkomsten (zeilboten, contributies) op 'Vrijgesteld' en kies per kostenpost of de BTW volledig, pro rata of niet aftrekbaar is.")}`}
          </p>
        </div>
      )}
      <p className="text-sm border-2 border-dashed border-secondary/60 bg-secondary/5 px-4 py-3 max-w-3xl" data-testid="btw-mixed-notice">
        <span className="font-display uppercase font-bold text-xs tracking-widest text-secondary block mb-1">{t("Gemengd BTW-regime")}</span>
        {t("Nieuw: het gemengde BTW-regime (bijvoorbeeld motorboot- naast zeilbootligplaatsen) is beschikbaar. Mis je iets of klopt een berekening niet met jouw aangifte? Laat het ons weten via")}{" "}
        <a href="mailto:info@club-fin.nl?subject=Gemengd%20BTW-regime" className="underline font-bold" data-testid="btw-mixed-contact">info@club-fin.nl</a>.
      </p>
    </div>
  );
}
